import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, ButtonGroup, Form } from "react-bootstrap";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import styles from "./PaintTool.module.css";

const TOOLS = [
  { id: "pen", icon: "bi-pencil-fill", title: "Qələm" },
  { id: "eraser", icon: "bi-eraser-fill", title: "Silgi" },
  { id: "line", icon: "bi-slash-lg", title: "Xətt" },
  { id: "rect", icon: "bi-square", title: "Düzbucaqlı" },
  { id: "ellipse", icon: "bi-circle", title: "Oval" },
  { id: "arrow", icon: "bi-arrow-up-right", title: "Ox" },
  { id: "text", icon: "bi-fonts", title: "Mətn" },
];

const SWATCHES = ["#111827", "#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#7e3af2", "#ffffff"];

function applyShape(ctx, tool, start, end, { color, strokeWidth, fill }) {
  ctx.lineWidth = strokeWidth;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (tool === "line") {
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    return;
  }

  if (tool === "rect") {
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);
    if (fill) ctx.fillRect(x, y, w, h);
    ctx.strokeRect(x, y, w, h);
    return;
  }

  if (tool === "ellipse") {
    const cx = (start.x + end.x) / 2;
    const cy = (start.y + end.y) / 2;
    const rx = Math.abs(end.x - start.x) / 2;
    const ry = Math.abs(end.y - start.y) / 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    if (fill) ctx.fill();
    ctx.stroke();
    return;
  }

  if (tool === "arrow") {
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    const headLen = Math.max(10, strokeWidth * 4);
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(end.x - headLen * Math.cos(angle - Math.PI / 6), end.y - headLen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(end.x - headLen * Math.cos(angle + Math.PI / 6), end.y - headLen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  }
}

const PaintTool = React.forwardRef(({ paintId }, ref) => {
  const storageKey = useRef(
    `teacher_toolbar_paint_${paintId || `paint_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`}`
  ).current;

  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const startPointRef = useRef({ x: 0, y: 0 });
  const isFirstSizeRef = useRef(true);

  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#111827");
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [fillShape, setFillShape] = useState(false);
  // textBox: { left, top, value, fontSize, color, mode: "editing" | "adjust" }
  // "editing" — yazı yazılır; "adjust" — yerbəyer olunub, sürüklənə/böyüdülə bilər, hələ kətana köçürülməyib
  const [textBox, setTextBox] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [clearConfirmShow, setClearConfirmShow] = useState(false);

  const textBoxRef = useRef(null);
  useEffect(() => { textBoxRef.current = textBox; }, [textBox]);

  const pushUndo = useCallback((dataURL) => {
    setUndoStack((prev) => [...prev.slice(-24), dataURL]);
    setRedoStack([]);
  }, []);

  const isCommittingTextRef = useRef(false);
  const textDragRef = useRef(null); // { type: "move" | "resize", startX, startY, startLeft, startTop, startFontSize }

  const measureTextBox = useCallback((value, fontSize) => {
    const canvas = canvasRef.current;
    if (!canvas) return { width: 60, height: Math.round(fontSize * 1.3) };
    const ctx = canvas.getContext("2d");
    const prevFont = ctx.font;
    ctx.font = `${fontSize}px Arial`;
    const width = Math.max(20, ctx.measureText(value || " ").width);
    ctx.font = prevFont;
    return { width, height: Math.round(fontSize * 1.3) };
  }, []);

  const openTextInput = (event) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const wrapperRect = wrapper.getBoundingClientRect();
    isCommittingTextRef.current = false;
    setTextBox({
      left: event.clientX - wrapperRect.left,
      top: event.clientY - wrapperRect.top,
      value: "",
      fontSize: 12 + strokeWidth * 3,
      color,
      mode: "editing",
    });
  };

  // Yazılan mətni kətana köçürür (varsa) və seçimi ləğv edir — yeni mətn açmadan, tool
  // dəyişmədən, saxlamadan/ixracdan əvvəl çağırılır ki, gözləyən mətn itməsin.
  const finalizeTextBoxNow = useCallback(() => {
    const current = textBoxRef.current;
    if (!current) return;
    if (current.value.trim()) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      pushUndo(canvas.toDataURL());
      ctx.font = `${current.fontSize}px Arial`;
      ctx.fillStyle = current.color;
      ctx.textBaseline = "top";
      ctx.fillText(current.value, current.left, current.top);
    }
    setTextBox(null);
    textBoxRef.current = null;
  }, [pushUndo]);

  // Yazı qutusundan çıxanda (Enter/blur) — mətni "adjust" rejiminə keçirir ki,
  // istifadəçi kənarından tutub böyüdə/kiçildə və sürüyə bilsin
  const commitTextEditing = () => {
    if (!textBox || textBox.mode !== "editing" || isCommittingTextRef.current) return;
    isCommittingTextRef.current = true;
    if (!textBox.value.trim()) {
      setTextBox(null);
      return;
    }
    setTextBox((prev) => (prev ? { ...prev, mode: "adjust" } : prev));
  };

  const startTextMove = (event) => {
    event.stopPropagation();
    event.preventDefault();
    textDragRef.current = {
      type: "move",
      startX: event.clientX,
      startY: event.clientY,
      startLeft: textBox.left,
      startTop: textBox.top,
    };
  };

  const startTextResize = (event) => {
    event.stopPropagation();
    event.preventDefault();
    textDragRef.current = {
      type: "resize",
      startX: event.clientX,
      startY: event.clientY,
      startFontSize: textBox.fontSize,
    };
  };

  // Sürükləmə/böyütmə üçün tək dəfə qeydiyyatdan keçən qlobal pointer izləyiciləri
  useEffect(() => {
    const onMove = (event) => {
      const drag = textDragRef.current;
      if (!drag) return;
      if (drag.type === "move") {
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        setTextBox((prev) => (prev ? { ...prev, left: drag.startLeft + dx, top: drag.startTop + dy } : prev));
      } else if (drag.type === "resize") {
        const dy = event.clientY - drag.startY;
        const nextSize = Math.min(160, Math.max(8, Math.round(drag.startFontSize + dy)));
        setTextBox((prev) => (prev ? { ...prev, fontSize: nextSize } : prev));
      }
    };
    const onUp = () => { textDragRef.current = null; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  // "adjust" rejimində Escape — mətni kətana köçürmədən ləğv edir
  useEffect(() => {
    if (!textBox || textBox.mode !== "adjust") return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setTextBox(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [textBox]);

  const getPoint = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
    };
  }, []);

  const saveToStorage = useCallback(() => {
    finalizeTextBoxNow();
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      localStorage.setItem(storageKey, canvas.toDataURL("image/png"));
    } catch (e) {
      console.error("localStorage xətası:", e);
    }
  }, [storageKey, finalizeTextBoxNow]);

  const loadFromStorage = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const saved = localStorage.getItem(storageKey);
    if (!saved) return;
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = saved;
  }, [storageKey]);

  useImperativeHandle(ref, () => ({ save: saveToStorage }), [saveToStorage]);

  // Kətan ölçüsünü konteynerə uyğunlaşdır, ölçü dəyişəndə mövcud şəkli saxla
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    const preview = previewCanvasRef.current;
    if (!wrapper || !canvas || !preview) return;

    const resize = () => {
      const rect = wrapper.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      if (canvas.width === w && canvas.height === h) return;

      if (isFirstSizeRef.current) {
        canvas.width = w;
        canvas.height = h;
        preview.width = w;
        preview.height = h;
        isFirstSizeRef.current = false;
        loadFromStorage();
        return;
      }

      const snapshot = canvas.toDataURL();
      canvas.width = w;
      canvas.height = h;
      preview.width = w;
      preview.height = h;
      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
      };
      img.src = snapshot;
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrapper);
    return () => ro.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Avtomatik saxlama + bağlanmadan əvvəl saxlama
  useEffect(() => {
    const interval = setInterval(saveToStorage, 4000);
    const onVisibility = () => { if (document.hidden) saveToStorage(); };
    window.addEventListener("beforeunload", saveToStorage);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", saveToStorage);
      document.removeEventListener("visibilitychange", onVisibility);
      saveToStorage();
    };
  }, [saveToStorage]);

  const restoreImage = useCallback((dataURL) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = dataURL;
  }, []);

  const undo = () => {
    const canvas = canvasRef.current;
    if (!canvas || undoStack.length === 0) return;
    const current = canvas.toDataURL();
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    setRedoStack((s) => [...s, current]);
    restoreImage(prev);
  };

  const redo = () => {
    const canvas = canvasRef.current;
    if (!canvas || redoStack.length === 0) return;
    const current = canvas.toDataURL();
    const next = redoStack[redoStack.length - 1];
    setRedoStack((s) => s.slice(0, -1));
    setUndoStack((s) => [...s, current]);
    restoreImage(next);
  };

  const drawPreview = (start, end) => {
    const preview = previewCanvasRef.current;
    if (!preview) return;
    const ctx = preview.getContext("2d");
    ctx.clearRect(0, 0, preview.width, preview.height);
    applyShape(ctx, tool, start, end, { color, strokeWidth, fill: fillShape });
  };

  const clearPreview = () => {
    const preview = previewCanvasRef.current;
    if (!preview) return;
    preview.getContext("2d").clearRect(0, 0, preview.width, preview.height);
  };

  const handlePointerDown = (event) => {
    // Kətan fokuslana bilən element olmadığı üçün brauzer mousedown-un default
    // hərəkəti kimi fokusu body-yə keçirir — bu, mətn input-unu dərhal blur edirdi.
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (tool === "text") {
      finalizeTextBoxNow();
      openTextInput(event);
      return;
    }

    const point = getPoint(event);
    pushUndo(canvas.toDataURL());
    isDrawingRef.current = true;
    startPointRef.current = point;

    if (tool === "pen" || tool === "eraser") {
      const ctx = canvas.getContext("2d");
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    }
  };

  const handlePointerMove = (event) => {
    if (!isDrawingRef.current) return;
    const point = getPoint(event);

    if (tool === "pen" || tool === "eraser") {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = color;
      ctx.globalCompositeOperation = tool === "eraser" ? "destination-out" : "source-over";
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      return;
    }

    drawPreview(startPointRef.current, point);
  };

  const handlePointerUp = (event) => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (tool === "pen" || tool === "eraser") {
      const ctx = canvas.getContext("2d");
      ctx.closePath();
      ctx.globalCompositeOperation = "source-over";
      return;
    }

    const point = getPoint(event);
    const ctx = canvas.getContext("2d");
    applyShape(ctx, tool, startPointRef.current, point, { color, strokeWidth, fill: fillShape });
    clearPreview();
  };

  const confirmClear = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      pushUndo(canvas.toDataURL());
      canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    }
    setTextBox(null);
    setClearConfirmShow(false);
  };

  const exportImage = () => {
    finalizeTextBoxNow();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const temp = document.createElement("canvas");
    temp.width = canvas.width;
    temp.height = canvas.height;
    const tctx = temp.getContext("2d");
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, temp.width, temp.height);
    tctx.drawImage(canvas, 0, 0);
    const link = document.createElement("a");
    link.href = temp.toDataURL("image/png");
    link.download = "resim.png";
    link.click();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <ButtonGroup size="sm">
          {TOOLS.map(({ id, icon, title }) => (
            <Button
              key={id}
              variant="light"
              className={tool === id ? styles.toolBtnActive : ""}
              onClick={() => { finalizeTextBoxNow(); setTool(id); }}
              title={title}
            >
              <i className={`bi ${icon}`} />
            </Button>
          ))}
        </ButtonGroup>

        <div className={styles.toolbarDivider} />

        <div className="d-flex align-items-center gap-1" title="Rəng">
          <Form.Control
            type="color"
            value={color}
            onChange={(event) => setColor(event.target.value)}
            className={styles.colorInput}
          />
          <div className={styles.swatchGroup}>
            {SWATCHES.map((swatch) => (
              <button
                key={swatch}
                type="button"
                className={`${styles.swatchBtn} ${color === swatch ? styles.swatchBtnActive : ""}`}
                style={{ backgroundColor: swatch }}
                onClick={() => setColor(swatch)}
                title={swatch}
              />
            ))}
          </div>
        </div>

        <div className={styles.toolbarDivider} />

        <div className="d-flex align-items-center gap-2" style={{ minWidth: 130 }} title="Fırça ölçüsü">
          <i className="bi bi-slash-lg" style={{ color: "#6b7280", fontSize: 12 }} />
          <Form.Range
            min={1}
            max={40}
            value={strokeWidth}
            onChange={(event) => setStrokeWidth(Number(event.target.value))}
            style={{ width: 90 }}
          />
          <span style={{ fontSize: 11, color: "#6b7280", width: 20 }}>{strokeWidth}</span>
        </div>

        {(tool === "rect" || tool === "ellipse") && (
          <>
            <div className={styles.toolbarDivider} />
            <Form.Check
              type="switch"
              id="paint-fill-switch"
              label="Doldur"
              checked={fillShape}
              onChange={(event) => setFillShape(event.target.checked)}
              className={styles.fillSwitch}
            />
          </>
        )}

        <div className={styles.toolbarDivider} />

        <ButtonGroup size="sm">
          <Button variant="light" onClick={undo} disabled={undoStack.length === 0} title="Geri al">
            <i className="bi bi-arrow-counterclockwise" />
          </Button>
          <Button variant="light" onClick={redo} disabled={redoStack.length === 0} title="İrəli al">
            <i className="bi bi-arrow-clockwise" />
          </Button>
        </ButtonGroup>

        <Button variant="outline-secondary" size="sm" onClick={() => setClearConfirmShow(true)}>
          <i className="bi bi-eraser me-1" />
          Təmizlə
        </Button>

        <Button variant="outline-primary" size="sm" onClick={exportImage} className="ms-auto">
          <i className="bi bi-download me-1" />
          Endir
        </Button>
      </div>

      <div ref={wrapperRef} className={styles.canvasWrapper}>
        <canvas
          ref={canvasRef}
          className={styles.baseCanvas}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
        <canvas ref={previewCanvasRef} className={styles.previewCanvas} />

        {textBox && (() => {
          const size = measureTextBox(textBox.value, textBox.fontSize);
          const isEditing = textBox.mode === "editing";
          return (
            <div
              className={styles.textBoxWrap}
              style={{ left: textBox.left - 4, top: textBox.top - 2, width: size.width + (isEditing ? 14 : 8), height: size.height + 4 }}
              onPointerDown={(event) => { if (event.target === event.currentTarget) startTextMove(event); }}
              onDoubleClick={() => {
                if (!isEditing) {
                  isCommittingTextRef.current = false;
                  setTextBox((prev) => (prev ? { ...prev, mode: "editing" } : prev));
                }
              }}
              title={isEditing ? undefined : "Sürükləyərək köçür, iki dəfə klikləyərək redaktə et"}
            >
              {isEditing ? (
                <input
                  ref={(node) => node?.focus()}
                  className={styles.textBoxInput}
                  style={{ color: textBox.color, fontSize: textBox.fontSize, width: size.width + 6 }}
                  value={textBox.value}
                  onChange={(event) => setTextBox((prev) => (prev ? { ...prev, value: event.target.value } : prev))}
                  onBlur={commitTextEditing}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") { event.preventDefault(); commitTextEditing(); }
                    if (event.key === "Escape") { event.preventDefault(); setTextBox(null); }
                  }}
                />
              ) : (
                <span style={{ fontSize: textBox.fontSize, color: textBox.color, fontFamily: "Arial", lineHeight: 1.3, whiteSpace: "pre" }}>
                  {textBox.value}
                </span>
              )}
              <div className={styles.textResizeHandle} onPointerDown={startTextResize} title="Ölçünü dəyiş" />
            </div>
          );
        })()}
      </div>

      <ConfirmModal
        show={clearConfirmShow}
        onHide={() => setClearConfirmShow(false)}
        onConfirm={confirmClear}
        title="Kətan təmizlənsin?"
        message="Bütün çəkilmiş şəkil silinəcək. Davam etmək istəyirsiniz?"
        confirmText="Təmizlə"
        cancelText="Ləğv et"
        variant="danger"
      />
    </div>
  );
});

PaintTool.defaultProps = {
  paintId: null,
};

export default PaintTool;
